import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';

/**
 * Komponent wyboru daty — port 1:1 z SellCall (apps/admin/src/components/ui/DatePicker.tsx).
 *
 * Standalone, bez zewnętrznych bibliotek (zero dodatkowych deps). Renderuje
 * input z ikoną + dropdown kalendarza w portalu na document.body (żeby nie być
 * przyciętym przez parent overflow:hidden).
 *
 * Pierwszym konsumentem jest popup filtra per kolumna w nagłówku tabeli
 * (tryb "zakres od-do" dla pól typu data) — dolewamy go w kolejnym sub-commicie.
 */

interface DatePickerProps {
  value: string; // YYYY-MM-DD format
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  clearable?: boolean;
  minDate?: string;
  maxDate?: string;
}

const MONTHS = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
];

const DAYS = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'];

export function DatePicker({
  value,
  onChange,
  placeholder = 'Wybierz datę',
  disabled = false,
  className = '',
  error = false,
  clearable = true,
  minDate,
  maxDate,
}: DatePickerProps) {
  const { t: _t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) {
      const [year, month] = value.split('-').map(Number);
      return { year: year!, month: month! - 1 };
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  const updateDropdownPos = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Esc zamyka kalendarz tylko, gdy jest otwarty. Faza przechwytywania + `preventDefault`: okno
  // nakładkowe (`Overlay`) widzi wtedy Esc jako już obsłużony i nie zamyka się razem z kalendarzem.
  useEffect(() => {
    if (!isOpen) return;
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isOpen]);

  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    return `${day!.toString().padStart(2, '0')}.${month!.toString().padStart(2, '0')}.${year}`;
  };

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number): number => {
    const day = new Date(year, month, 1).getDay();
    // Convert Sunday (0) to 6, Monday (1) to 0, etc.
    return day === 0 ? 6 : day - 1;
  };

  const isDateDisabled = (year: number, month: number, day: number): boolean => {
    const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;
    return false;
  };

  const handlePrevMonth = () => {
    setViewDate((prev) => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { ...prev, month: prev.month - 1 };
    });
  };

  const handleNextMonth = () => {
    setViewDate((prev) => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { ...prev, month: prev.month + 1 };
    });
  };

  const handleSelectDate = (day: number) => {
    const dateStr = `${viewDate.year}-${(viewDate.month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    if (!isDateDisabled(viewDate.year, viewDate.month, day)) {
      onChange(dateStr);
      setIsOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(viewDate.year, viewDate.month);
    const firstDay = getFirstDayOfMonth(viewDate.year, viewDate.month);
    const days: React.ReactElement[] = [];

    // Empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-8 h-8" />);
    }

    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${viewDate.year}-${(viewDate.month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const isSelected = value === dateStr;
      const isDisabled = isDateDisabled(viewDate.year, viewDate.month, day);
      const isToday = dateStr === new Date().toISOString().split('T')[0];

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleSelectDate(day)}
          disabled={isDisabled}
          className={`
            w-8 h-8 rounded-full text-sm flex items-center justify-center transition-colors
            ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-accent-100'}
            ${isSelected ? 'bg-accent-600 text-white hover:bg-accent-700' : ''}
            ${isToday && !isSelected ? 'border border-accent-400 text-accent-600' : ''}
          `}
        >
          {day}
        </button>,
      );
    }

    return days;
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (!disabled) {
            if (!isOpen) updateDropdownPos();
            setIsOpen(!isOpen);
          }
        }}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between gap-2 px-3 py-2
          bg-white dark:bg-gray-800 border rounded-lg text-left
          transition-colors
          ${disabled ? 'bg-gray-100 dark:bg-gray-900 cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-gray-400 dark:hover:border-gray-500'}
          ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-accent-500 dark:focus:ring-accent-400'}
          ${isOpen ? 'ring-2 ring-accent-500 dark:ring-accent-400 border-accent-500 dark:border-accent-400' : ''}
        `}
      >
        <div className="flex items-center gap-2 flex-1">
          <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          <span
            className={
              value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
            }
          >
            {value ? formatDisplayDate(value) : placeholder}
          </span>
        </div>
        {value && clearable && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            <X className="w-3 h-3 text-gray-400 dark:text-gray-500" />
          </button>
        )}
      </button>

      {isOpen &&
        dropdownPos &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 w-72"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {MONTHS[viewDate.month]} {viewDate.year}
              </span>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Day names */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map((day) => (
                <div
                  key={day}
                  className="w-8 h-8 flex items-center justify-center text-xs font-medium text-gray-500"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">{renderCalendarDays()}</div>

            {/* Quick actions */}
            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  if (today) onChange(today);
                  setIsOpen(false);
                }}
                className="flex-1 px-3 py-1.5 text-sm text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/30 rounded-lg transition-colors"
              >
                Dzisiaj
              </button>
              {clearable && (
                <button
                  type="button"
                  onClick={() => {
                    onChange('');
                    setIsOpen(false);
                  }}
                  className="flex-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Wyczyść
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default DatePicker;
